import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '@/lib/i18n';
import { useWorkspace } from '@/lib/workspace';
import { ListChecks, CalendarBlank, Kanban, ChartBar, GearSix, Clock } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import OfflineIndicator from '@/components/OfflineIndicator';

function WorkspaceSwitcher({ compact = false }) {
  const { workspaces, activeId, setActiveId, multi } = useWorkspace();
  if (!multi) return null;
  return (
    <Select value={activeId || ''} onValueChange={setActiveId}>
      <SelectTrigger data-testid="workspace-switcher" className={cn('h-8 text-xs', compact ? 'w-full' : 'w-full')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {workspaces.map(w => (
          <SelectItem key={w.id} value={w.id}>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: w.color || '#CA8A04' }} />
              {w.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const BASE_NAV = [
  { path: '/list', icon: ListChecks, label: 'nav.list' },
  { path: '/calendar', icon: CalendarBlank, label: 'nav.calendar' },
  { path: '/circle', icon: Clock, label: 'nav.circle' },
  { path: '/kanban', icon: Kanban, label: 'nav.kanban' },
];
const TAIL_NAV = [
  { path: '/analytics', icon: ChartBar, label: 'nav.analytics' },
  { path: '/settings', icon: GearSix, label: 'nav.settings' },
];

function NavItem({ item, active, onClick }) {
  const { t } = useI18n();
  const Icon = item.icon;
  return (
    <button data-testid={`nav-${item.path.slice(1)}`} onClick={onClick}
      className={cn('flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 w-full',
        active ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted')}>
      <Icon size={20} weight={active ? 'fill' : 'regular'} />
      <span className="hidden lg:inline">{t(item.label)}</span>
    </button>
  );
}

function MobileNavItem({ item, active, onClick }) {
  const { t } = useI18n();
  const Icon = item.icon;
  return (
    <button data-testid={`mobile-nav-${item.path.slice(1)}`} onClick={onClick}
      className={cn('flex flex-col items-center gap-0.5 text-xs transition-colors', active ? 'text-primary' : 'text-muted-foreground')}>
      <Icon size={22} weight={active ? 'fill' : 'regular'} />
      <span>{t(item.label)}</span>
    </button>
  );
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { multi: multiWorkspace } = useWorkspace();
  const navItems = [...BASE_NAV, ...TAIL_NAV];

  return (
    <div className="flex min-h-screen bg-background" data-testid="app-layout">
      <OfflineIndicator />
      <aside className="hidden md:flex w-16 lg:w-56 border-r border-border bg-card flex-col fixed left-0 top-0 h-screen z-40" data-testid="desktop-sidebar">
        <div className="p-2 lg:px-3 flex items-center justify-center lg:justify-start border-b border-border h-14">
          <img src="/logo.png" alt="Tododuk" className="h-10 w-auto lg:h-12 object-contain" />
        </div>
        {multiWorkspace && (
          <div className="hidden lg:block p-3 border-b border-border">
            <WorkspaceSwitcher />
          </div>
        )}
        <nav className="flex-1 p-2 lg:p-3 space-y-1" data-testid="sidebar-nav">
          {navItems.map(item => (
            <NavItem key={item.path} item={item} active={location.pathname === item.path} onClick={() => navigate(item.path)} />
          ))}
        </nav>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden md:ml-16 lg:ml-56 pb-20 md:pb-0 min-h-screen">
        <div className="md:hidden px-3 pt-2"><WorkspaceSwitcher compact /></div>
        <Outlet />
      </main>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 border-t border-border bg-card/95 backdrop-blur-md flex items-center justify-around z-50 px-2" data-testid="mobile-nav">
        {navItems.map(item => (
          <MobileNavItem key={item.path} item={item} active={location.pathname === item.path} onClick={() => navigate(item.path)} />
        ))}
      </nav>
    </div>
  );
}
